// react
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import LocationCityRoundedIcon from '@mui/icons-material/LocationCityRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import TollRoundedIcon from '@mui/icons-material/TollRounded';

// material ui
import {
  Box,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material';

// api
import {
  useGetEnergyGroupQuery,
  usePutEnergyGroupEnergyTreeNodeOptionsMutation,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// feature-components
import DeclarationCard from '@/features/energyxplorer/components/esg/DeclarationCard';
import EsgDistributionPieChart from '@/features/energyxplorer/components/esg/EsgDistributionPieChart';
import EsgInitiativesDatagrid from '@/features/energyxplorer/components/esg/EsgInitiativesDatagrid';
import GoCard from '@/features/energyxplorer/components/esg/GoCard';
import GoSharesDatagrid from '@/features/energyxplorer/components/esg/GoSharesDatagrid';
import KpiCard from '@/features/energyxplorer/components/esg/KpiCard';
import SeuDatagrid from '@/features/energyxplorer/components/esg/SeuDatagrid';
import YearOnYearGraph from '@/features/energyxplorer/components/esg/YearOnYearGraph';

// feature-hooks
import { useEsgUrlDateRange } from '@/features/energyxplorer/hooks/useEsgUrlDateRange';

// feature-utils
import { getRootNode } from '@/features/energyxplorer/utils/energyTreeFunctions';

// relative
import {
  useGetCO2eEmissionDataQuery,
  useGetCO2eEmissionPerNodeQuery,
  useGetCO2eEmissionTimestampSummaryQuery,
} from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';
import { showSnackbar } from '../../../../redux/snackbarSlice';

import { DateTime } from 'luxon';

const ESG_ZONE = 'Europe/Copenhagen';

const normalizeEmissionsData = (data) => {
  if (!data) return data;
  return data.map((month) => {
    const monthData = month.Data;
    return {
      ...month,
      Data: monthData.map((item) => ({
        ...item,
        CO2ePerUnit: item.CO2ePerUnit || item.CO2ePerkWh,
        CO2e: item.CO2e || item.CO2eEnergy,
      })),
    };
  });
};

const ESGPage = () => {
  const { t } = useTranslation('energyxplorer-esg');

  const { accountId, energyGroupId } = useParams({ strict: false });
  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    { skip: !energyGroupId },
  );
  // const [isTimestampValid, setTimestampValid] = useState(false);
  // ESG-specific date range with its own query params (esgFrom/esgTo)
  const { from, to, timeRange, setRange } = useEsgUrlDateRange({
    zone: ESG_ZONE,
  });

  const dispatch = useDispatch();

  const {
    data: timestampsSummary,
    isSuccess: isTimestampsSummarySuccess,
    isFetching: isTimestampsSummaryFetching,
    isError: isTimestampsSummaryError,
  } = useGetCO2eEmissionTimestampSummaryQuery(
    {
      accountId: accountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
      timerange: timeRange,
    },
    {
      skip: !accountId || !selectedEnergyGroup || !timeRange,
    },
  );

  const dataWindow = useMemo(() => {
    if (!isTimestampsSummarySuccess || !timestampsSummary) return null;
    if (!from || !to) return null;

    const zone = from.zoneName || ESG_ZONE;
    const { maxEarliestDataTimestamp, minLatestDataTimestamp } =
      timestampsSummary;
    if (!maxEarliestDataTimestamp || !minLatestDataTimestamp) return null;

    const maxEarliest = DateTime.fromISO(maxEarliestDataTimestamp).setZone(
      zone,
    );
    const minLatest = DateTime.fromISO(minLatestDataTimestamp).setZone(zone);

    if (!maxEarliest.isValid || !minLatest.isValid) return null;

    return { maxEarliest, minLatest };
  }, [isTimestampsSummarySuccess, timestampsSummary, from, to]);

  const isTimestampValid = useMemo(() => {
    if (!dataWindow) return false;
    return from >= dataWindow.maxEarliest && to <= dataWindow.minLatest;
  }, [dataWindow, from, to]);

  const {
    data: esgOverviewData,
    isError: isEsgOverviewDataError,
    isFetching: isEsgOverviewDataFetching,
    isSuccess: isEsgOverviewDataSuccess,
  } = useGetCO2eEmissionDataQuery(
    {
      accountId: accountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
      start: timeRange?.start,
      end: timeRange?.end,
    },
    {
      skip:
        !accountId ||
        selectedEnergyGroup === null ||
        !timeRange ||
        timeRange.start == null ||
        timeRange.end == null ||
        !isTimestampValid,
    },
  );
  const isPortfolioEnergyGroup =
    selectedEnergyGroup && selectedEnergyGroup.type?.includes('Portfolio');
  const { data: esgNodeData, isFetching: isEsgNodeDataFetching } =
    useGetCO2eEmissionPerNodeQuery(
      {
        accountId: accountId,
        energyGroupId: selectedEnergyGroup?.energyGroupId,
        start: timeRange?.start,
        end: timeRange?.end,
      },
      {
        skip:
          !accountId ||
          selectedEnergyGroup === null ||
          !isPortfolioEnergyGroup ||
          !timeRange ||
          timeRange.start == null ||
          timeRange.end == null ||
          !isTimestampValid,
      },
    );
  const modes = [
    {
      id: 0,
      areaType: 'location',
      icon: <PlaceRoundedIcon />,
      energySummary: esgOverviewData?.Location?.CO2eEmissionsSummary,
    },
    {
      id: 1,
      areaType: 'municipality',
      icon: <LocationCityRoundedIcon />,
      energySummary: esgOverviewData?.Municipality?.CO2eEmissionsSummary,
    },
    {
      id: 2,
      areaType: 'market',
      icon: <TollRoundedIcon />,
      energySummary: esgOverviewData?.Market?.CO2eEmissionsSummary,
    },
  ];
  const [selectedMode, setSelectedMode] = React.useState(0);

  const scopeData = useMemo(() => {
    switch (selectedMode) {
      case 0:
        return esgOverviewData?.Location;
      case 1:
        return esgOverviewData?.Municipality;
      case 2:
        return esgOverviewData?.Market;
      default:
        return null;
    }
  }, [selectedMode, esgOverviewData]);

  const gridMixSummary = useMemo(() => scopeData?.GridMixSummary, [scopeData]);
  const reShare = useMemo(
    () => scopeData?.REShareSummary?.REShare,
    [scopeData],
  );
  const gridMixData = useMemo(
    () => scopeData?.GridMixDistributionData,
    [scopeData],
  );

  const nodeScopeData = useMemo(() => {
    if (!isPortfolioEnergyGroup || !esgNodeData) return [];
    let data;
    switch (selectedMode) {
      case 0:
        data = esgNodeData?.Location;
        break;
      case 1:
        data = esgNodeData?.Municipality;
        break;
      case 2:
        data = esgNodeData?.Market;
        break;
      default:
        return [];
    }
    return normalizeEmissionsData(data) || [];
  }, [isPortfolioEnergyGroup, esgNodeData, selectedMode]);

  const seuData = useMemo(
    () => normalizeEmissionsData(scopeData?.MonthlyCO2eEmissions),
    [scopeData],
  );

  // Clamp ESG URL date range to available data window from timestampsSummary
  useEffect(() => {
    if (!isTimestampsSummarySuccess || !timestampsSummary) return;
    if (!from || !to) return;

    const zone = from.zoneName || ESG_ZONE;

    const { maxEarliestDataTimestamp, minLatestDataTimestamp } =
      timestampsSummary;

    if (!maxEarliestDataTimestamp || !minLatestDataTimestamp) return;

    const maxEarliest = DateTime.fromISO(maxEarliestDataTimestamp).setZone(
      zone,
    );
    const minLatest = DateTime.fromISO(minLatestDataTimestamp).setZone(zone);

    if (!maxEarliest.isValid || !minLatest.isValid) return;

    const currentStart = from;
    const currentEnd = to;

    const isCompletelyOutside =
      currentEnd < maxEarliest || currentStart > minLatest;

    let adjustedStart;
    let adjustedEnd;

    if (isCompletelyOutside) {
      // Full fallback: select full available window
      adjustedStart = maxEarliest;
      adjustedEnd = minLatest;
    } else {
      // Normal adjustment: clip only start or end
      adjustedStart = currentStart < maxEarliest ? maxEarliest : currentStart;
      adjustedEnd = currentEnd > minLatest ? minLatest : currentEnd;

      // Extra safety: if adjustment somehow causes inverted range
      if (adjustedStart > adjustedEnd) {
        adjustedStart = maxEarliest;
        adjustedEnd = minLatest;
      }
    }

    const noChange =
      adjustedStart.toISO() === currentStart.toISO() &&
      adjustedEnd.toISO() === currentEnd.toISO();

    if (noChange) return;

    // Avoid extra clamping: we've already chosen the range, so disable clampToNow and rounding
    setRange(adjustedStart, adjustedEnd, {
      roundToDay: false,
      clampToNow: false,
    });

    dispatch(
      showSnackbar({
        severity: 'info',
        message: t('snackbar.timerange_changed'),
      }),
    );
  }, [
    isTimestampsSummarySuccess,
    timestampsSummary,
    from,
    to,
    setRange,
    dispatch,
    t,
  ]);

  const [openGoShares, setOpenGoShares] = useState(false);

  const handleCloseGoShares = () => {
    setOpenGoShares(false);
  };
  const rootNode = getRootNode(selectedEnergyGroup?.energyTreeNodes || []);
  const [putEnergyTreeNodeOptions, { isLoading: isUpdating }] =
    usePutEnergyGroupEnergyTreeNodeOptionsMutation();
  const handleSaveChanges = async (goShares) => {
    if (!selectedEnergyGroup?.energyGroupId) return;
    const options = rootNode?.options;

    const updates = {
      ...options,
      goShares: goShares,
    };

    const result = await putEnergyTreeNodeOptions({
      groupId: selectedEnergyGroup.energyGroupId,
      nodeId: rootNode.energyTreeNodeId,
      putEnergyTreeNodeOptionsDto: updates,
    });

    if (!('error' in result)) {
      dispatch(showSnackbar({ message: 'Opdateret', severity: 'success' }));
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  if (
    isEsgOverviewDataFetching ||
    isEsgNodeDataFetching ||
    isTimestampsSummaryFetching ||
    !timeRange
  ) {
    return (
      <Box height={'60vh'}>
        <GatewayLoadingIndicator
          isLoading={
            isEsgOverviewDataFetching ||
            isEsgNodeDataFetching ||
            isTimestampsSummaryFetching ||
            !timeRange
          }
          loadingText={t('loading_page_text')}
          disableBackdrop={true}
        />
      </Box>
    );
  }

  if (isEsgOverviewDataError || isTimestampsSummaryError) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Card
          width={{ xs: '100%', sm: '80%', md: '60%', lg: '60%' }}
          sx={{ p: 2, mb: 2, mt: 8, borderRadius: 2 }}
        >
          <CardContent width={{ xs: '100%', sm: '80%', md: '60%', lg: '60%' }}>
            <Typography variant="h6">{t('error_card.title')}</Typography>
            <Typography variant="body1" color="text.secondary">
              {t('error_card.description')}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <PageContainer sx={{ pt: 0 }}>
      {isEsgOverviewDataSuccess && (
        <Box>
          <Grid container spacing={2} paddingTop={0}>
            {modes.map((mode) => (
              <Grid
                key={mode.id}
                className={mode.id === selectedMode ? '' : 'non-printable'}
                size={{
                  md: 12,
                  lg: 6,
                  xl: 3
                }}>
                <Suspense
                  fallback={<div>{t('actions.loading', { ns: 'common' })}</div>}
                >
                  <DeclarationCard
                    key={mode.id}
                    {...mode}
                    timeRange={timeRange}
                    mode={mode.id}
                    setMode={setSelectedMode}
                    selected={mode.id === selectedMode}
                  />
                </Suspense>
              </Grid>
            ))}
            <Grid
              size={{
                md: 12,
                lg: 6,
                xl: 3
              }}>
              {selectedMode === 2 ? (
                <Suspense
                  fallback={<div>{t('actions.loading', { ns: 'common' })}</div>}
                >
                  <GoCard
                    openGoShares={setOpenGoShares}
                    number={reShare}
                    timeRange={timeRange}
                  />
                </Suspense>
              ) : (
                <Suspense
                  fallback={<div>{t('actions.loading', { ns: 'common' })}</div>}
                >
                  <KpiCard
                    title={t('re_card.label')}
                    description={t('re_card.description')}
                    number={reShare}
                    unit="%"
                    timeRange={timeRange}
                  />
                </Suspense>
              )}
            </Grid>
            {!isPortfolioEnergyGroup && (
              <Grid
                sx={{ transition: 'flex-basis 0.3s ease' }}
                size={{
                  md: 12
                }}>
                <Suspense fallback={<Skeleton />}>
                  <EsgInitiativesDatagrid
                    selectedEnergyGroup={selectedEnergyGroup}
                    timerangeMS={timeRange}
                  />
                </Suspense>
              </Grid>
            )}
            {selectedMode !== 2 && (
              <Suspense
                fallback={<div>{t('actions.loading', { ns: 'common' })}</div>}
              >
                <EsgDistributionPieChart
                  GridMixData={gridMixData}
                  GridMixLocationSummary={gridMixSummary}
                  timeRange={timeRange}
                />
              </Suspense>
            )}
            {isPortfolioEnergyGroup &&
            Array.isArray(nodeScopeData) &&
            nodeScopeData.length > 0 ? (
              <Grid
                sx={{ transition: 'flex-basis 0.3s ease' }}
                size={{
                  md: 12
                }}>
                <Suspense
                  fallback={<div>{t('actions.loading', { ns: 'common' })}</div>}
                >
                  <SeuDatagrid data={[...seuData, ...nodeScopeData]} />
                </Suspense>
              </Grid>
            ) : (
              <Grid
                sx={{ transition: 'flex-basis 0.3s ease' }}
                size={{
                  md: 12
                }}>
                <Suspense
                  fallback={<div>{t('actions.loading', { ns: 'common' })}</div>}
                >
                  <SeuDatagrid data={seuData} />
                </Suspense>
              </Grid>
            )}

            <Grid
              sx={{ transition: 'flex-basis 0.3s ease' }}
              size={{
                md: 12
              }}>
              <Suspense
                fallback={<div>{t('actions.loading', { ns: 'common' })}</div>}
              >
                <YearOnYearGraph data={seuData} />
              </Suspense>
            </Grid>
          </Grid>

          <Suspense
            fallback={<div>{t('actions.loading', { ns: 'common' })}</div>}
          >
            <GoSharesDatagrid
              handleSaveChanges={handleSaveChanges}
              onClose={handleCloseGoShares}
              open={openGoShares}
              isFetching={isUpdating}
              nodeId={rootNode?.energyTreeNodeId}
            />
          </Suspense>
        </Box>
      )}
    </PageContainer>
  );
};

export default ESGPage;
